import { Router } from "express";
import multer from "multer";
import path from "path";
import fs from "fs";
import { db } from "@workspace/db";
import { notificationSettingsTable, auditLogTable, activityEventsTable, documentsTable } from "@workspace/db";
import { eq, and, desc, sql } from "drizzle-orm";
import { requireAuth, type AuthRequest } from "../lib/auth";

const uploadDir = path.join(process.cwd(), "uploads");
if (!fs.existsSync(uploadDir)) fs.mkdirSync(uploadDir, { recursive: true });

const docStorage = multer.diskStorage({
  destination: (_req, _file, cb) => cb(null, uploadDir),
  filename: (_req, file, cb) => cb(null, `${Date.now()}-${file.originalname.replace(/[^a-zA-Z0-9._-]/g, "_")}`),
});
const docUpload = multer({ storage: docStorage, limits: { fileSize: 20 * 1024 * 1024 } });

const router = Router();

router.get("/notifications/settings", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(404).json({ error: "No company" });

    const settings = await db
      .select()
      .from(notificationSettingsTable)
      .where(eq(notificationSettingsTable.companyId, req.companyId))
      .limit(1);

    if (!settings[0]) {
      const created = await db
        .insert(notificationSettingsTable)
        .values({ companyId: req.companyId })
        .returning();
      return res.json(created[0]);
    }

    res.json(settings[0]);
  } catch (err) {
    next(err);
  }
});

router.put("/notifications/settings", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company" });

    const { deadlineReminder7Days, deadlineReminder1Day, analysisComplete, subscriptionEvents } = req.body;

    const existing = await db
      .select()
      .from(notificationSettingsTable)
      .where(eq(notificationSettingsTable.companyId, req.companyId))
      .limit(1);

    if (!existing[0]) {
      const created = await db
        .insert(notificationSettingsTable)
        .values({ companyId: req.companyId, deadlineReminder7Days, deadlineReminder1Day, analysisComplete, subscriptionEvents })
        .returning();
      return res.json(created[0]);
    }

    const updated = await db
      .update(notificationSettingsTable)
      .set({ deadlineReminder7Days, deadlineReminder1Day, analysisComplete, subscriptionEvents, updatedAt: new Date() })
      .where(eq(notificationSettingsTable.id, existing[0].id))
      .returning();

    res.json(updated[0]);
  } catch (err) {
    next(err);
  }
});

router.get("/audit-log", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    const { tenderId, page = "1", limit = "50" } = req.query as Record<string, string>;
    const offset = (parseInt(page) - 1) * parseInt(limit);

    // Always scope to the authenticated company; optionally further filter by tenderId
    if (!req.companyId) return res.status(400).json({ error: "No company" });

    const whereClause = tenderId
      ? and(eq(auditLogTable.companyId, req.companyId), eq(auditLogTable.tenderId, Number(tenderId)))
      : eq(auditLogTable.companyId, req.companyId);

    const entries = await db
      .select()
      .from(auditLogTable)
      .where(whereClause)
      .orderBy(desc(auditLogTable.createdAt))
      .limit(parseInt(limit))
      .offset(offset);

    res.json(entries);
  } catch (err) {
    next(err);
  }
});


router.get("/documents", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.json([]);
    const docs = await db
      .select()
      .from(documentsTable)
      .where(eq(documentsTable.companyId, req.companyId));
    res.json(docs);
  } catch (err) {
    next(err);
  }
});

router.post("/documents", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company" });
    const doc = await db
      .insert(documentsTable)
      .values({ ...req.body, companyId: req.companyId })
      .returning();
    res.status(201).json(doc[0]);
  } catch (err) {
    next(err);
  }
});

router.post("/documents/upload", requireAuth, docUpload.single("file"), async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company" });
    const file = req.file;
    if (!file) return res.status(400).json({ error: "No file uploaded" });

    // Migrate columns if not yet present
    await db.execute(sql`ALTER TABLE documents ADD COLUMN IF NOT EXISTS description TEXT`);
    await db.execute(sql`ALTER TABLE documents ADD COLUMN IF NOT EXISTS expires_at TIMESTAMP`);

    // Fix multer latin1 mis-interpretation of UTF-8 filenames (Arabic, etc.)
    let originalName = file.originalname;
    try {
      const decoded = Buffer.from(originalName, "latin1").toString("utf8");
      if (!decoded.includes("\uFFFD")) originalName = decoded;
    } catch { /* keep as-is */ }

    const name = (req.body.name as string) || originalName;
    const description = (req.body.description as string) || null;
    const expiresAt = req.body.expiresAt ? new Date(req.body.expiresAt) : null;

    const doc = await db
      .insert(documentsTable)
      .values({
        companyId: req.companyId,
        name,
        description,
        fileName: originalName,
        fileSize: file.size,
        filePath: file.path,
        ...(expiresAt && !isNaN(expiresAt.getTime()) ? { expiresAt } : {}),
      })
      .returning();
    res.status(201).json(doc[0]);
  } catch (err) {
    next(err);
  }
});

router.get("/documents/:docId/file", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(403).json({ error: "No company" });
    const [doc] = await db
      .select()
      .from(documentsTable)
      .where(and(eq(documentsTable.id, Number(req.params.docId)), eq(documentsTable.companyId, req.companyId)))
      .limit(1);
    if (!doc || !doc.filePath) return res.status(404).json({ error: "Not found" });
    res.sendFile(path.resolve(doc.filePath));
  } catch (err) {
    next(err);
  }
});

router.patch("/documents/:docId", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(403).json({ error: "No company" });
    const { name, description, expiresAt } = req.body;
    const expiresAtDate = expiresAt ? new Date(expiresAt) : null;
    const updated = await db
      .update(documentsTable)
      .set({
        ...(name !== undefined ? { name } : {}),
        ...(description !== undefined ? { description } : {}),
        ...(expiresAt !== undefined ? { expiresAt: expiresAtDate && !isNaN(expiresAtDate.getTime()) ? expiresAtDate : null } : {}),
      })
      .where(and(eq(documentsTable.id, Number(req.params.docId)), eq(documentsTable.companyId, req.companyId)))
      .returning();
    if (!updated[0]) return res.status(404).json({ error: "Not found" });
    res.json(updated[0]);
  } catch (err) {
    next(err);
  }
});

router.delete("/documents/:docId", requireAuth, async (req: AuthRequest, res, next) => {
  try {
    if (!req.companyId) return res.status(400).json({ error: "No company" });

    // Only delete documents that belong to the authenticated company
    const deleted = await db
      .delete(documentsTable)
      .where(and(eq(documentsTable.id, Number(req.params.docId)), eq(documentsTable.companyId, req.companyId)))
      .returning({ id: documentsTable.id });

    if (!deleted[0]) return res.status(404).json({ error: "Not found" });
    res.status(204).send();
  } catch (err) {
    next(err);
  }
});

export default router;
